import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Manifesto block — appears right after hero carousel on Home.
// Full-bleed, cinematic. References the 1902 Paris→Mers bourgeoisie train.
export default function ManifesteBlock() {
    return (
        <section className="relative bg-cp-encre text-cp-ivoire overflow-hidden" data-testid="manifeste-block">
            {/* Left: image | Right: copy — stacks on mobile */}
            <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[80vh]">
                {/* Visual column */}
                <div className="lg:col-span-5 relative min-h-[60vh] lg:min-h-full cp-grain">
                    <img
                        src={`${BACKEND}/api/static/img/hero_train_mers_1902_v2.jpg`}
                        alt="Train Paris — Mers-les-Bains, 1902"
                        className="absolute inset-0 w-full h-full object-cover cp-photo-warm"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-cp-encre/70 via-transparent to-cp-encre/30" />
                    <div className="absolute bottom-8 left-8 right-8 flex flex-col gap-2 text-cp-ivoire/80">
                        <span className="cp-tiny text-cp-sable">Archive · 1902</span>
                        <p className="cp-serif italic text-lg leading-snug max-w-sm">
                            « Le train du samedi partait à 8h17 de la gare du Nord.<br />
                            La côte se laissait découvrir avant midi. »
                        </p>
                    </div>
                </div>

                {/* Copy column */}
                <div className="lg:col-span-7 px-6 md:px-16 lg:px-24 py-20 md:py-32 flex flex-col justify-center">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <p className="cp-tiny text-cp-sable mb-10">Manifeste · Côte Perdue</p>

                        <h2 className="cp-serif text-5xl md:text-7xl lg:text-[104px] leading-[0.92] tracking-[-0.03em] text-cp-ivoire">
                            Le <em className="cp-display-italic text-cp-sable">retour</em><br />
                            d'un territoire <em className="cp-display-italic text-cp-sable">vrai</em>.
                        </h2>

                        <div className="mt-14 space-y-7 cp-serif text-xl md:text-2xl leading-[1.55] font-light text-cp-ivoire/90 max-w-3xl">
                            <p>
                                En <em className="cp-display-italic text-cp-sable">1902</em>, la bourgeoisie d'affaires prenait le train de 8h17 pour
                                Mers-les-Bains. Près de <em className="cp-display-italic">cinq cents villas</em> polychromes s'élevaient déjà entre Ault et Le Tréport.
                                On dînait au Casino, on se baignait à marée haute. <em className="cp-display-italic">C'était chic, sans en faire trop.</em>
                            </p>
                            <p>
                                Puis le siècle a tourné. Le Sud a pris la lumière,
                                et la côte s'est refermée sur elle-même — <em className="cp-display-italic text-cp-sable">elle nous a attendus.</em>
                            </p>
                            <p>
                                Côte Perdue est un <em className="cp-display-italic">magazine vivant</em> : des adresses vérifiées, une cartographie interactive,
                                un agenda réel relié à OpenAgenda. Nous ne vendons rien à votre place — chaque fiche renvoie à la source officielle.
                            </p>
                            <p className="cp-serif italic text-cp-sable text-2xl md:text-3xl pt-4">
                                Le train part toujours à 8h17.<br />
                                Il ne reste qu'à monter.
                            </p>
                        </div>

                        <div className="mt-14 flex flex-wrap items-center gap-6">
                            <Link
                                to="/manifeste"
                                className="cp-btn-outline-light"
                                data-testid="manifeste-read-full"
                            >
                                Lire le manifeste →
                            </Link>
                            <span className="cp-tiny text-cp-ivoire/50">
                                6 min de lecture · signé la rédaction
                            </span>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
